import {
    GetLocationDocument,
} from '../../types/db';
import { accessor } from '../store';
import { messages } from 'src/i18n/i18n';
import {
    RouteEntityDetail,
} from '@redviking/argonaut-core-ui/src/components/EntityDetail';
import { Route } from 'vue-router';
import { RouterViewSlider } from '@redviking/argonaut-core-ui/src/components/RouterViewSlider';
import { validateLocationSelectAcl, validateLocationUpdateAcl } from './locations.validations';
import { entityDetailRoute } from '../components/EntityDetail';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { Notify } from '../notifications';
import { entityPageBannerTitle } from 'src/util/formatting';
import {
    LocationEntity,
    createLocation,
    saveLocation,
} from './locations.entity';
import type { RouteConfig } from 'vue-router';

const locationRoutes: RouteConfig[] = [
    {
        path: '/locations',
        name: 'locations',
        component: RouterViewSlider,
        redirect: { name: 'locationList' },
        meta: {
            navigationGuard: () => validateLocationSelectAcl(),
            navbarTitle: messages.locations.titles.nav,
            navbarIcon: 'mdi-map-marker',
            transition: 'horizontal',
            featureFlags: [],
        },
        children: [
            {
                name: 'locationList',
                path: '',
                component: () => import(/* webpackChunkName: "locations" */ './LocationsView.vue'),
                meta: {
                    pageBannerTitle: messages.locations.titles.pageBannerTitle,
                    pageBannerSubtitle: messages.locations.titles.pageBannerSubtitle,
                },
            },
            entityDetailRoute<'location'>({
                path: ':locationId',
                name: 'locationMaintenence',
                component: RouteEntityDetail,
                redirect: to => ({ name: 'locationSettings', params: to.params }),
                meta: {
                    returnRoute: (route: Route) => ({ name: 'locationList', query: route.query }),
                    canEdit: () => validateLocationUpdateAcl(),
                    pageBannerTitle: () => entityPageBannerTitle('location', accessor.entityAsType('location')?.name),
                    entityType: 'location',
                    saveEntity: saveLocation,
                    createEntity: createLocation,
                    getEntity: async (to) => {
                        if (to.query.mode === 'create') {
                            const entityData: LocationEntity = {
                                is_default: false,
                                calendarLinks: [],
                                id: to.params.locationId,
                                name: 'Unnamed Location',
                                description: '',
                                parentLocationId: null,
                                location_attributes: [],
                            };
                            return {
                                entity: entityData,
                            };
                        }
                        const { location } = await gqlClient.request({
                            document: GetLocationDocument,
                            variables: {
                                locationId: to.params.locationId,
                            },
                        });
                        if (location) {
                            const ret: LocationEntity = {
                                id: location.id,
                                name: location.name,
                                is_default: location.is_default,
                                description: location.description,
                                calendarLinks: location.calendarLinks,
                                parentLocationId: location.parentLocationId,
                                location_class_id: location.location_class_id,
                                location_attributes: location.location_attributes.map(la => la.attribute.id),
                            };
                            return {
                                entity: ret,
                            };
                        } else {
                            const err = new Error('Unknown Location');
                            Notify.error(err);
                            throw err;
                        }
                    },
                },
                children: [
                    {
                        path: 'settings',
                        name: 'locationSettings',
                        component: () => import(/* webpackChunkName: "locations" */ './LocationSettings.view.vue'), // a component for the settings page content
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Settings',
                                icon: 'mdi-cog',
                            },
                            navigationGuard: () => true,
                        },
                        // navigationGuard: () => validateLocationUpdateAcl(),
                    },
                    {
                        path: 'location-attribute-list',
                        name: 'locationAttributeList',
                        component: () => import(/* webpackChunkName: "locations" */ './LocationAttributeList.view.vue'), // a component for the settings page content
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Attributes',
                                icon: 'mdi-animation-outline',
                            },
                            navigationGuard: () => Boolean(accessor.featureFlags.attributes),
                        },
                    },
                    {
                        path: 'location-schedule',
                        name: 'locationSchedule',
                        component: () => import(/* webpackChunkName: "schedule" */ './LocationSchedule.view.vue'), // a component for the settings page content
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Calendar',
                                icon: 'mdi-calendar',
                            },
                            navigationGuard: () => Boolean(accessor.featureFlags.schedules),
                            pageBannerTitle: () => entityPageBannerTitle('location', accessor.entityAsType('location')?.name),
                        },
                    },
                    {
                        path: 'location-attribute-history',
                        name: 'locationAttributeHistory',
                        component: () => import(/* webpackChunkName: "locations" */ './LocationAttributeHistory.view.vue'),
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Attribute History',
                                icon: 'mdi-history',
                            },
                            navigationGuard: () => Boolean(accessor.featureFlags.attributes),
                            pageBannerTitle: () => entityPageBannerTitle('location', accessor.entityAsType('location')?.name),
                        },
                    },
                ],
            }),
        ],
    },
];

export default locationRoutes;
